import { Product } from 'models/product'
import { useMemo } from 'react'
import { create } from 'zustand'

interface SelectedProductState {
  selectedProducts: Product[]
}

interface SelectedProductActions {
  setSelectedProducts: (products: Product[]) => void
  toggleProduct: (product: Product) => void
}

export type SelectedProductStore = SelectedProductState & SelectedProductActions

export const useSelectedProductsStore = create<SelectedProductStore>()(
  (set) => ({
    selectedProducts: [],
    setSelectedProducts: (products) => set({ selectedProducts: products }),
    toggleProduct: (product) =>
      set((state) => {
        const isSelected = state.selectedProducts.some(
          (p) => p.id === product.id
        )
        return {
          selectedProducts: isSelected
            ? state.selectedProducts.filter((p) => p.id !== product.id)
            : [...state.selectedProducts, product],
        }
      }),
  })
)

export function useSelectedProductIds() {
  const selectedProducts = useSelectedProductsStore(
    (state) => state.selectedProducts
  )
  const selectedProductIds = useMemo(
    () => selectedProducts.map((p) => p.id),
    [selectedProducts]
  )
  return selectedProductIds
}

/**
 * The "all products" sentinel id. Kept in sync with the backend
 * (`ALL_PRODUCTS_SENTINEL` in
 * `server/opportunities/views/unified/detail_providers/google_place_filters.py`
 * and the legacy `/reports/*` summary params) — a `product_id` of `-1` means "no
 * product filter", so it is dropped before the ids are sent as a filter.
 */
export const ALL_PRODUCTS_ID = -1

/**
 * Drop the "all products" sentinel so what remains is the real product filter
 * (empty = no filter). Send this to any endpoint that takes `product_id`.
 *
 * @param selectedProductIds - Ids from {@link useSelectedProductIds}.
 * @returns The selected ids with the {@link ALL_PRODUCTS_ID} sentinel removed.
 */
export function activeProductIds(selectedProductIds: number[]): number[] {
  return selectedProductIds.filter((id) => id !== ALL_PRODUCTS_ID)
}
